import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  Directive,
  ElementRef,
  inject,
  OnDestroy,
  output,
  PLATFORM_ID,
} from '@angular/core';

export interface ResizeSize {
  width: number;
  height: number;
}

export interface ResizePosition {
  x: number;
  y: number;
}

export interface ResizeResult {
  size: ResizeSize;
  position: ResizePosition;
}

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export const RESIZE_MIN_W = 400;
export const RESIZE_MIN_H = 600;

const CURSOR_MAP: Record<ResizeDirection, string> = {
  n: 'n-resize',
  s: 's-resize',
  e: 'e-resize',
  w: 'w-resize',
  ne: 'ne-resize',
  nw: 'nw-resize',
  se: 'se-resize',
  sw: 'sw-resize',
};

@Directive({
  selector: '[appResizable]',
  standalone: true,
})
export class ResizableDirective implements OnDestroy {
  readonly resized = output<ResizeResult>();

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly platform = inject(PLATFORM_ID);

  private isResizing = false;
  private direction: ResizeDirection = 'se';
  private startX = 0;
  private startY = 0;
  private origW = 0;
  private origH = 0;
  private origLeft = 0;
  private origTop = 0;

  private handles: HTMLElement[] = [];

  private rafId: number | null = null;
  private pendingEvent: MouseEvent | null = null;

  private readonly onMouseMove = (e: MouseEvent) => this.queueResize(e);
  private readonly onMouseUp = () => this.stopResize();

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platform)) return;
      this.createHandles();
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
    });
  }

  ngOnDestroy(): void {
    this.handles.forEach((h) => h.remove());
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private createHandles(): void {
    const directions: ResizeDirection[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
    const EDGE = '6px';
    const CORNER = '14px';

    directions.forEach((dir) => {
      const handle = document.createElement('div');
      handle.setAttribute('data-resize-dir', dir);
      handle.setAttribute('aria-hidden', 'true');
      handle.style.cssText = `position:absolute;z-index:20;cursor:${CURSOR_MAP[dir]};user-select:none;`;

      switch (dir) {
        case 'n':
          Object.assign(handle.style, { top: '0', left: CORNER, right: CORNER, height: EDGE });
          break;
        case 's':
          Object.assign(handle.style, { bottom: '0', left: CORNER, right: CORNER, height: EDGE });
          break;
        case 'e':
          Object.assign(handle.style, { top: CORNER, bottom: CORNER, right: '0', width: EDGE });
          break;
        case 'w':
          Object.assign(handle.style, { top: CORNER, bottom: CORNER, left: '0', width: EDGE });
          break;
        case 'ne':
          Object.assign(handle.style, { top: '0', right: '0', width: CORNER, height: CORNER });
          break;
        case 'nw':
          Object.assign(handle.style, { top: '0', left: '0', width: CORNER, height: CORNER });
          break;
        case 'se':
          Object.assign(handle.style, { bottom: '0', right: '0', width: CORNER, height: CORNER });
          break;
        case 'sw':
          Object.assign(handle.style, { bottom: '0', left: '0', width: CORNER, height: CORNER });
          break;
      }

      handle.addEventListener('mousedown', (e) => this.startResize(e, dir));
      this.el.nativeElement.appendChild(handle);
      this.handles.push(handle);
    });
  }

  /** Gọi từ component khi toggle fullscreen */
  setHandlesVisible(visible: boolean): void {
    this.handles.forEach((h) => (h.style.display = visible ? '' : 'none'));
  }

  private startResize(e: MouseEvent, dir: ResizeDirection): void {
    this.isResizing = true;
    this.direction = dir;
    this.startX = e.clientX;
    this.startY = e.clientY;

    const rect = this.el.nativeElement.getBoundingClientRect();
    this.origW = rect.width;
    this.origH = rect.height;
    this.origLeft = rect.left;
    this.origTop = rect.top;

    document.body.style.cursor = CURSOR_MAP[dir];
    document.body.style.userSelect = 'none';

    e.preventDefault();
    e.stopPropagation();
  }

  private stopResize(): void {
    if (!this.isResizing) return;
    this.isResizing = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.pendingEvent = null;
  }

  /** Gom nhiều sự kiện mousemove trong cùng 1 frame lại thành 1 lần tính toán, tránh reflow liên tục */
  private queueResize(e: MouseEvent): void {
    if (!this.isResizing) return;

    this.pendingEvent = e;
    if (this.rafId !== null) return;

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      if (this.pendingEvent) {
        this.onResize(this.pendingEvent);
        this.pendingEvent = null;
      }
    });
  }

  private onResize(e: MouseEvent): void {
    if (!this.isResizing) return;

    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;
    const dir = this.direction;

    let newW = this.origW;
    let newH = this.origH;
    let newX = this.origLeft;
    let newY = this.origTop;

    // Width & X
    if (dir.includes('e')) {
      newW = this.origW + dx;
    }
    if (dir.includes('w')) {
      newW = this.origW - dx;
      newX = this.origLeft + dx;
    }

    // Height & Y
    if (dir.includes('s')) {
      newH = this.origH + dy;
    }
    if (dir === 'n' || dir === 'ne' || dir === 'nw') {
      newH = this.origH - dy;
      newY = this.origTop + dy;
    }

    // Clamp min — khi kéo các cạnh trái/trên thì giữ cạnh kia cố định
    if (newW < RESIZE_MIN_W) {
      if (dir.includes('w')) newX = this.origLeft + this.origW - RESIZE_MIN_W;
      newW = RESIZE_MIN_W;
    }
    if (newH < RESIZE_MIN_H) {
      if (dir === 'n' || dir === 'ne' || dir === 'nw')
        newY = this.origTop + this.origH - RESIZE_MIN_H;
      newH = RESIZE_MIN_H;
    }

    // Clamp max — không vượt viewport
    if (newX < 0) {
      newW += newX;
      newX = 0;
    }
    if (newY < 0) {
      newH += newY;
      newY = 0;
    }
    if (newX + newW > window.innerWidth) newW = window.innerWidth - newX;
    if (newY + newH > window.innerHeight) newH = window.innerHeight - newY;

    // Đảm bảo min sau cùng
    newW = Math.max(RESIZE_MIN_W, newW);
    newH = Math.max(RESIZE_MIN_H, newH);

    this.resized.emit({
      size: { width: newW, height: newH },
      position: { x: newX, y: newY },
    });
  }
}
